/**
 * Invite someone to the organization by address and role. Invitations are
 * redeemed by signing in with the invited address; when the deployment sends
 * email the invitee gets a message, otherwise the dialog turns into a receipt
 * with the sign-in link to pass on.
 *
 * @module components/settings/InviteMemberDialog
 */
import { CheckIcon, CopyIcon, MailCheckIcon } from "lucide-react";
import { useState } from "react";

import { invitationSignInLink, type InviteResult } from "../../state/orgProfile";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Radio, RadioGroup } from "../ui/radio-group";

type InviteRole = "admin" | "member";

/** Loose on purpose — the server is the judge; this only catches obvious slips. */
export const looksLikeEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export function InviteMemberDialog({
  open,
  onOpenChange,
  emailEnabled,
  invite,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Whether the deployment emails invitations itself. */
  readonly emailEnabled: boolean;
  readonly invite: (payload: { email: string; role: InviteRole }) => Promise<InviteResult>;
}) {
  const [lastOpen, setLastOpen] = useState(open);
  const [session, setSession] = useState(0);
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) setSession((value) => value + 1);
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup className="max-w-md">
        <InviteForm
          key={session}
          onOpenChange={onOpenChange}
          emailEnabled={emailEnabled}
          invite={invite}
        />
      </DialogPopup>
    </Dialog>
  );
}

function InviteForm({
  onOpenChange,
  emailEnabled,
  invite,
}: {
  readonly onOpenChange: (open: boolean) => void;
  readonly emailEnabled: boolean;
  readonly invite: (payload: { email: string; role: InviteRole }) => Promise<InviteResult>;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<InviteRole>("member");
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<Extract<InviteResult, { ok: true }> | null>(null);
  const address = email.trim();
  const valid = looksLikeEmail(address);

  const submit = () => {
    if (!valid || isSending) return;
    setIsSending(true);
    setError(null);
    void invite({ email: address, role }).then((outcome) => {
      setIsSending(false);
      if (outcome.ok) setReceipt(outcome);
      else setError(outcome.message);
    });
  };

  if (receipt) {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Invitation sent</DialogTitle>
          <DialogDescription>
            {receipt.delivery === "email"
              ? "They join by signing in with that address. The invitation lasts two weeks."
              : "Send them the sign-in link. They join by signing in with that address; the invitation lasts two weeks."}
          </DialogDescription>
        </DialogHeader>
        <DialogPanel className="space-y-3">
          <div className="flex items-center gap-3 rounded-lg border border-border/70 px-3 py-2.5">
            <MailCheckIcon className="size-4 shrink-0 text-success" aria-hidden />
            <p className="min-w-0 truncate text-sm text-foreground">
              {receipt.delivery === "email" ? "Emailed " : "Recorded for "}
              <strong className="font-medium">{receipt.invitation.email}</strong>
            </p>
          </div>
          {receipt.delivery === "email" ? null : <SignInLinkCopy />}
        </DialogPanel>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => setReceipt(null)}>
            Invite another
          </Button>
          <Button type="button" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </>
    );
  }

  return (
    <form
      className="flex min-h-0 flex-col"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <DialogHeader>
        <DialogTitle>Invite to organization</DialogTitle>
        <DialogDescription>
          {emailEnabled
            ? "They get an email and join by signing in with that address."
            : "You will get a sign-in link to send them. They join by signing in with that address."}
        </DialogDescription>
      </DialogHeader>
      <DialogPanel className="space-y-5">
        <label className="block space-y-1.5">
          <span className="text-sm font-medium">Email address</span>
          <Input
            type="email"
            autoFocus
            autoComplete="off"
            placeholder="name@company.com"
            value={email}
            disabled={isSending}
            aria-invalid={(address.length > 0 && !valid) || undefined}
            onChange={(event) => {
              setEmail(event.currentTarget.value);
              setError(null);
            }}
          />
        </label>
        <div className="space-y-1.5">
          <span className="text-sm font-medium">Role</span>
          <RadioGroup
            value={role}
            onValueChange={(value) => setRole(value as InviteRole)}
            aria-label="Role"
            className="grid gap-1.5 sm:grid-cols-2"
          >
            {(
              [
                ["member", "Member", "Works in the organization."],
                ["admin", "Admin", "Also manages members, limits and branding."],
              ] as const
            ).map(([value, title, hint]) => (
              <label
                key={value}
                className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-border/70 px-3 py-2.5 transition-colors hover:bg-muted/40 has-data-checked:border-primary/40 has-data-checked:bg-primary/[0.04]"
              >
                <Radio value={value} className="mt-0.5" disabled={isSending} />
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{title}</span>
                  <span className="mt-0.5 block text-xs leading-4 text-muted-foreground">
                    {hint}
                  </span>
                </span>
              </label>
            ))}
          </RadioGroup>
        </div>
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </DialogPanel>
      <DialogFooter>
        <Button type="button" variant="ghost" disabled={isSending} onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button type="submit" disabled={!valid || isSending}>
          {isSending ? "Inviting…" : "Send invitation"}
        </Button>
      </DialogFooter>
    </form>
  );
}

/** The link an invitee signs in through, with a copy button. */
export function SignInLinkCopy() {
  const [copied, setCopied] = useState(false);
  const link = invitationSignInLink(window.location.origin);
  const copy = () => {
    void navigator.clipboard?.writeText(link).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      },
      () => undefined,
    );
  };
  return (
    <div className="flex items-center gap-2">
      <code className="min-w-0 flex-1 truncate rounded-md bg-muted/60 px-2 py-1.5 text-[11px]">
        {link}
      </code>
      <Button size="xs" variant="outline" onClick={copy} aria-label="Copy sign-in link">
        {copied ? <CheckIcon className="size-3.5" /> : <CopyIcon className="size-3.5" />}
        {copied ? "Copied" : "Copy"}
      </Button>
    </div>
  );
}
